import React from "react";
import { useForm } from "react-hook-form";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Loader2, Mail } from "lucide-react";
import SectionHeading from "@/components/site/SectionHeading";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm();
    const [done, setDone] = React.useState(false);

    const onSubmit = async (data) => {
        try {
            await axios.post(`${API}/contact`, data);
            setDone(true);
            toast.success("Message sent. We&rsquo;ll be in touch shortly.");
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (e) {
            toast.error("Could not send your message. Please try again.");
        }
    };

    return (
        <div data-testid="contact-page">
            <section className="bg-indigo_brand text-white">
                <div className="ih-container py-20 md:py-24">
                    <p className="ih-eyebrow" style={{ color: "#08C020" }}>Contact</p>
                    <h1 className="mt-3 font-montserrat font-bold text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                        Let&rsquo;s talk about
                        <span className="block text-green_brand">what&rsquo;s next for your people.</span>
                    </h1>
                    <p className="mt-7 font-opensans text-white/80 text-lg leading-relaxed max-w-2xl">
                        For partnership inquiries, speaking, or anything else — leave us a note. We respond within one business day.
                    </p>
                </div>
            </section>

            <section className="ih-section bg-surface">
                <div className="ih-container grid gap-10 lg:grid-cols-[1fr_1.4fr]">
                    <div className="lg:sticky lg:top-28">
                        <SectionHeading eyebrow="Reach Us" title="One inbox. Real humans." />
                        <a
                            href="mailto:info@theintuitushub.com"
                            data-testid="contact-email-link"
                            className="mt-8 inline-flex items-center gap-3 ih-card p-5"
                        >
                            <span className="inline-flex w-10 h-10 items-center justify-center rounded-lg bg-green_brand/15">
                                <Mail size={18} className="text-green_brand" />
                            </span>
                            <div>
                                <p className="font-poppins text-[10px] uppercase tracking-[0.18em] text-[#4A5568]">Email</p>
                                <p className="font-raleway font-semibold text-indigo_brand">info@theintuitushub.com</p>
                            </div>
                        </a>
                        <div className="mt-8 ih-card p-6">
                            <p className="ih-eyebrow">Looking for something specific?</p>
                            <ul className="mt-4 space-y-3 font-opensans text-sm text-indigo_brand">
                                <li>• Take the <Link to="/snapshot" className="underline decoration-green_brand underline-offset-4">HUMAN+ Snapshot™</Link></li>
                                <li>• Register for the <Link to="/foundations" className="underline decoration-green_brand underline-offset-4">Foundations Experience™</Link></li>
                                <li>• Schedule a <Link to="/discovery" className="underline decoration-green_brand underline-offset-4">Discovery Call</Link></li>
                            </ul>
                        </div>
                    </div>

                    {done ? (
                        <div data-testid="contact-thanks" className="ih-card p-10 shadow-medium">
                            <span className="inline-flex w-12 h-12 items-center justify-center rounded-xl bg-green_brand/15">
                                <Check size={22} className="text-green_brand" />
                            </span>
                            <h3 className="mt-5 font-montserrat font-bold text-indigo_brand text-2xl">
                                Message received.
                            </h3>
                            <p className="mt-3 font-opensans text-[#4A5568] text-base leading-relaxed">
                                Thank you. We&rsquo;ll reply within one business day from info@theintuitushub.com.
                            </p>
                            <Link to="/" className="ih-btn-secondary mt-7">Return Home</Link>
                        </div>
                    ) : (
                        <form
                            onSubmit={handleSubmit(onSubmit)}
                            data-testid="contact-form"
                            className="ih-card p-8 md:p-10 shadow-medium grid gap-5 sm:grid-cols-2"
                        >
                            <div style={{ display: "none" }} aria-hidden="true">
                                <input
                                    type="text"
                                    tabIndex={-1}
                                    autoComplete="off"
                                    {...register("hp_field")}
                                />
                            </div>
                            <Field label="Full Name *" error={errors.name} className="sm:col-span-2">
                                <Input data-testid="contact-name" {...register("name", { required: true })} placeholder="Jordan Reyes" className="h-12" />
                            </Field>
                            <Field label="Email *" error={errors.email}>
                                <Input data-testid="contact-email" type="email" {...register("email", { required: true, pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/ })} placeholder="name@organization.com" className="h-12" />
                            </Field>
                            <Field label="Organization">
                                <Input data-testid="contact-organization_name" {...register("organization_name")} placeholder="Acme, Inc." className="h-12" />
                            </Field>
                            <Field label="Your message *" error={errors.message} className="sm:col-span-2">
                                <Textarea data-testid="contact-message" {...register("message", { required: true, minLength: 5 })} rows={5} placeholder="A line or two about what you&rsquo;d like to discuss." />
                            </Field>

                            <div className="sm:col-span-2 pt-2 flex justify-end">
                                <button type="submit" disabled={isSubmitting} data-testid="contact-submit" className="ih-btn-primary disabled:opacity-60">
                                    {isSubmitting ? <><Loader2 className="animate-spin" size={16} /> Sending…</> : <>Send Message <ArrowRight size={16} /></>}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </section>
        </div>
    );
};

const Field = ({ label, error, children, className = "" }) => (
    <div className={className}>
        <Label className="font-raleway font-semibold text-indigo_brand text-sm">{label}</Label>
        <div className="mt-2">{children}</div>
        {error && <p className="mt-1 font-opensans text-xs text-red-600">This field is required.</p>}
    </div>
);

export default Contact;
